import { useState, useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CheckCircle2 } from 'lucide-react'
import { FormationProposedName } from '@/types'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  formationId: string
  proposedName: FormationProposedName | null | Record<string, any>
  basePath: string // will be `/backoffice` or empty (for user app)
}

export function FormationProposedNameSheet({ open, onOpenChange, formationId, proposedName, basePath }: Props) {
  const isEditing = !!proposedName
  const [success, setSuccess] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  const { data, setData, post, patch, processing, reset, clearErrors } = useForm({
    name: ''
  })

  useEffect(() => {
    if (open) {
      if (proposedName) {
        setData({
          name: proposedName.name
        })
      } else {
        reset()
      }
      setApiError(null)
      setSuccess(false)
      clearErrors()
    }
  }, [open, proposedName])

  function handleClose(value: boolean) {
    if (!value) {
      setSuccess(false)
      setApiError(null)
    }
    onOpenChange(value)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setApiError(null)

    const urlPath = basePath ? `${basePath}/formations/${formationId}` : `/formations/${formationId}`
    const url = isEditing
      ? `${urlPath}/proposed_names/${proposedName.id}`
      : `${urlPath}/proposed_names`

    const request = isEditing ? patch : post

    request(url, {
      preserveScroll: true,
      onSuccess: () => {
        setSuccess(true)
        setTimeout(() => handleClose(false), 1000)
      },
      onError: (errors) => {
        const errorMessages = Object.values(errors).filter(Boolean)
        setApiError(errorMessages.length > 0 ? errorMessages.join(', ') : 'Something went wrong')
      }
    })
  }

  if (success) {
    return (
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent side="right" className="sm:max-w-lg">
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-sm font-medium text-foreground">
              {isEditing ? 'Proposed name updated' : 'Proposed name added'}
            </p>
          </div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEditing ? 'Edit Proposed Name' : 'Add Proposed Name'}</SheetTitle>
          <SheetDescription>
            {isEditing
              ? `Update the proposed name details`
              : 'Add an alternative name for this formation entity'}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-5 px-4 mt-6">
          {apiError && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-lg border border-destructive/20">
              {apiError}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="name">Name <span className="text-destructive">*</span></Label>
            <Input
              id="name"
              value={data.name}
              onChange={e => setData('name', e.target.value)}
              placeholder="e.g. Acme Corp"
              required
              autoFocus
            />
          </div>

          <div className="sticky bottom-0 bg-background border-t p-4 -mx-4 mt-8">
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={processing || !data.name}>
                {processing ? 'Saving...' : isEditing ? 'Update Name' : 'Add Name'}
              </Button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
